import type { ReactNode } from 'react';

/**
 * Textes d'aide contextuelle, un par écran.
 *
 * RÈGLES D'ÉCRITURE — s'y tenir, sinon l'aide devient le bruit qu'elle devait
 * éviter :
 *
 *  - `what` : UNE phrase. À quoi sert l'écran et quand on y vient. Pas de
 *    reformulation du titre (« Ici vous gérez le planning » n'apprend rien).
 *  - `points` : QUATRE au maximum, et uniquement ce qui ne se devine pas —
 *    une règle métier, un mot dont le sens est particulier ici, un piège.
 *    Si un point décrit un bouton visible à l'écran, il ne sert à rien.
 *  - Ton : on explique, on ne vend pas. Phrases courtes, pas d'impératif
 *    permanent, pas de « il vous suffit de ».
 */
export interface HelpEntry {
  what: ReactNode;
  points: ReactNode[];
}

export const HELP = {
  // --- Titulaire ----------------------------------------------------------

  planning: {
    what: 'La grille de la semaine, salarié par salarié : qui travaille, quand, et où ça coince.',
    points: [
      <>
        <strong>Une case grise « — »</strong> n'est pas un oubli : le salarié n'est pas sous
        contrat ce jour-là. Sa ligne indique jusqu'à quand il l'est.
      </>,
      <>
        Le <strong>liseré ambre</strong> signale une journée qui déborde des horaires
        d'ouverture de l'officine.
      </>,
      <>
        <strong>Appui long puis glissement</strong> recopie les horaires d'une journée sur une
        autre — du même salarié uniquement, et c'est une copie : la journée d'origine ne bouge
        pas.
      </>,
      <>
        La <strong>baguette</strong> n'apparaît que si vous avez du personnel en horaires
        variables. Elle propose de les faire venir sur les trous.
      </>,
    ],
  },

  employees: {
    what: 'Votre équipe : contrats, compétences, semaines types et compteurs d\'heures.',
    points: [
      <>
        Une <strong>fiche salarié</strong> et un <strong>compte de connexion</strong> sont deux
        choses différentes. La fiche existe dès que vous l'ajoutez ; le compte n'existe que
        lorsque le salarié s'active avec le code d'invitation que vous lui remettez.
      </>,
      <>
        Les <strong>compétences</strong> décident qui peut tenir quelle mission, et donc qui le
        moteur a le droit de placer. Une compétence oubliée se traduit par des trous inexpliqués.
      </>,
      <>
        <strong>Archiver</strong> plutôt que supprimer : l'historique et les fiches navette
        passées restent lisibles.
      </>,
    ],
  },

  organization: {
    what: 'Qui fait quoi dans la journée : la répartition des présents entre les missions.',
    points: [
      <>
        C'est <strong>calculé, pas saisi</strong>. Le moteur part des présents du planning et les
        répartit selon leurs compétences et vos effectifs requis.
      </>,
      <>
        Un <strong>trou</strong> ici veut dire que personne de qualifié n'était disponible — pas
        que personne ne travaillait. La réponse est souvent une compétence à ajouter, ou un
        effectif requis trop élevé.
      </>,
    ],
  },

  appointments: {
    what: 'Les rendez-vous métier — vaccination, TROD, entretiens — et qui a le droit de les prendre.',
    points: [
      <>
        Le <strong>verdict s'affiche avant</strong> de valider : vert si des salariés qualifiés
        sont disponibles, rouge si personne ne l'est.
      </>,
      <>
        Vous pouvez <strong>désigner quelqu'un de non habilité</strong>, mais il faut le
        justifier, et le rendez-vous reste signalé en rouge dans la liste.
      </>,
      <>
        Les prestations proposées viennent de votre <strong>catalogue</strong> (Réglages) — c'est
        lui qui associe chaque prestation à la compétence requise.
      </>,
    ],
  },

  shuttle: {
    what: 'Le récapitulatif mensuel envoyé au comptable : heures supplémentaires, absences, congés.',
    points: [
      <>
        Chaque ligne montre son <strong>cumul</strong> et son <strong>report</strong> : le cumul
        d'un mois devient le report du suivant. C'est ce qui fait le lien d'un mois à l'autre.
      </>,
      <>
        Le solde affiché ici doit être le <strong>même</strong> que celui de la Balance des
        heures. S'ils diffèrent, ne tranchez pas — signalez-le.
      </>,
      <>
        Une fois <strong>envoyée</strong>, la fiche est archivée telle quelle dans l'historique :
        elle ne se recalcule plus.
      </>,
    ],
  },

  balance: {
    what: 'L\'écart entre les heures réellement faites et le contrat, mois par mois.',
    points: [
      <>
        Le <strong>solde de départ</strong> reprend ce qui existait avant Officina. À poser une
        fois, au démarrage.
      </>,
      <>
        <strong>Chaque case se déclare</strong>, comme sur la fiche navette : le calcul propose,
        vous écrivez ce que vous voulez. Une case déclarée passe en ambre, et
        « Revenir au calcul » la remet comme avant.
      </>,
      <>
        Déclarer ne touche à <strong>rien en dessous</strong> : ni les créneaux, ni les absences.
        C'est ce qui évite d'avoir à défaire les données du mois pour qu'un total retombe juste.
        Un mois déclaré déplace la balance globale avec lui.
      </>,
      <>
        Pour enregistrer un <strong>vrai mouvement d'heures</strong> — des heures données ou
        reprises, à une date —, c'est un ajustement, dans la fiche du salarié. Lui se propage
        (fiche navette, corrections vues par le salarié) ; une déclaration, non.
      </>,
    ],
  },

  validation: {
    what: 'Figer une semaine écoulée : après validation, les heures ne bougent plus.',
    points: [
      <>
        Une semaine validée devient <strong>non modifiable</strong>. C'est ce qui garantit que la
        fiche navette du mois ne changera plus dans le dos du comptable.
      </>,
      <>
        Un salarié qui a <strong>l'application</strong> est attendu à la signature. S'il n'a pas
        signé, la validation n'est pas interdite : elle <strong>demande confirmation</strong>, en
        nommant qui manque — et garde la trace de qui a été passé outre.
      </>,
      <>
        Un salarié <strong>sans compte</strong> n'est jamais attendu : il n'a aucun moyen de
        signer. Pour celui qui a l'application mais ne s'en servira pas, décochez
        <strong> « Sa signature est attendue »</strong> sur sa fiche : la question ne reviendra plus.
      </>,
    ],
  },

  // Pas d'entrée pour « Changer les horaires » : cet écran s'explique déjà
  // lui-même, en gros, sous son titre — y ajouter une bulle qui répète la même
  // chose serait précisément le bruit qu'on cherche à éviter.

  statistics: {
    what: 'Les tendances sur la durée : effectif prévisionnel, heures par salarié, répartition des missions.',
    points: [
      <>
        Un bloc <strong>vide</strong> n'est pas une panne : il n'y a simplement pas encore assez
        de données sur la période choisie.
      </>,
    ],
  },

  multisite: {
    what: 'Vos officines et le passage de l\'une à l\'autre.',
    points: [
      <>
        Les données restent <strong>cloisonnées par officine</strong>. Basculer change tout ce que
        vous voyez : planning, équipe, fiche navette.
      </>,
      <>
        Un <strong>salarié partagé</strong> reste rattaché à son officine d'origine. Il n'apparaît
        ailleurs que les jours où il y est prévu — il ne peut pas être à deux endroits le même
        jour.
      </>,
    ],
  },

  settings: {
    what: 'Les règles qui alimentent tout le reste : horaires, effectifs requis, prestations, primes.',
    points: [
      <>
        Les <strong>horaires d'ouverture</strong> bornent le reste : ils pré-remplissent la
        saisie et signalent les journées qui débordent.
      </>,
      <>
        Les <strong>effectifs requis</strong> sont un minimum, pas une cible. C'est en dessous
        qu'une demi-journée est signalée découverte.
      </>,
      <>
        L'<strong>accès en consultation</strong> laisse un autre titulaire voir votre planning. Ouvrez-le
        avant qu'il aille sur PharmaRH : sinon le site lui fait créer sa propre pharmacie.
      </>,
      <>
        La <strong>Zone de danger</strong>, en bas, efface définitivement l'officine. Elle demande
        de retaper son nom.
      </>,
    ],
  },

  requests: {
    what: 'Les demandes de vos salariés : congés, corrections d\'horaire.',
    points: [
      <>
        Accepter un congé <strong>libère le créneau</strong> : la couverture et la fiche navette
        s'ajustent aussitôt.
      </>,
    ],
  },

  // --- Salarié ------------------------------------------------------------

  myWeek: {
    what: 'Vos semaines à venir, et la signature qui indique que vous en avez pris connaissance.',
    points: [
      <>
        Signer ne vaut pas accord sur les heures : c'est un <strong>accusé de lecture</strong>.
        Vous pouvez retirer votre signature tant que la semaine n'est pas validée.
      </>,
    ],
  },

  myAvailability: {
    what: 'Les jours et heures où vous pouvez venir, si vous êtes en horaires variables.',
    points: [
      <>
        Ce n'est <strong>pas un planning</strong> : vous déclarez vos disponibilités, votre
        titulaire y puise selon les besoins.
      </>,
      <>
        Le total est une <strong>amplitude disponible</strong>, pas des heures dues. Il ne se
        compare pas à votre contrat.
      </>,
      <>
        Hors de ces créneaux, on peut quand même vous solliciter s'il ne reste personne — vous en
        serez averti.
      </>,
    ],
  },

  myHistory: {
    what: 'Vos heures, votre solde et les événements de votre dossier.',
    points: [
      <>
        La date affichée est celle <strong>d'effet</strong>, pas celle de saisie : c'est elle qui
        décide du mois de rattachement.
      </>,
    ],
  },
} satisfies Record<string, HelpEntry>;

export type HelpKey = keyof typeof HELP;
